// ==========================================================================
// Portfolio Section - Arc Carousel with GSAP
// ==========================================================================

import gsap from "gsap";

/**
 * Initialize Portfolio Arc Carousel
 * Creates a subtle arc effect with 3 visible slides (1 center + 2 sides)
 */
export function initPortfolioSection() {
    const container = document.querySelector(".portfolio-arc-container");
    if (!container) return;

    const items = document.querySelectorAll(".portfolio-arc-item");
    const prevBtn = document.querySelector(".portfolio-nav-prev");
    const nextBtn = document.querySelector(".portfolio-nav-next");

    if (!items.length) return;

    const totalItems = items.length;
    let currentIndex = 0;

    // Configuration - arc carousel with 3 visible slides
    const config = {
        visibleItems: 3,
        cardSpacing: 380,         // Horizontal spacing between cards (increased)
        arcHeight: 60,            // Vertical offset for arc effect (increased)
        sideScale: 0.82,          // Scale for side cards
        sideOpacity: 0.7,         // Opacity for side cards
        sideRotation: 10,         // Rotation for side cards (increased)
        duration: 0.7,
        ease: "power3.out",
        dragThreshold: 80,        // Minimum drag distance to trigger slide change
    };

    /**
     * Get modulo that works with negative numbers
     */
    function mod(n, m) {
        return ((n % m) + m) % m;
    }

    /**
     * Position all items based on current index
     */
    function updatePositions(animate = true) {
        items.forEach((item, i) => {
            // Calculate position relative to current index (with loop)
            let relativePos = i - currentIndex;

            // Wrap around for loop effect
            if (relativePos > totalItems / 2) relativePos -= totalItems;
            if (relativePos < -totalItems / 2) relativePos += totalItems;

            // Only show items within visible range
            const isVisible = Math.abs(relativePos) <= 1;

            // Calculate horizontal position
            const x = relativePos * config.cardSpacing;

            // Calculate vertical offset for subtle arc (parabola)
            const y = Math.abs(relativePos) * config.arcHeight;

            // Calculate scale
            const scale = relativePos === 0 ? 1 : config.sideScale;

            // Calculate opacity
            const opacity = relativePos === 0 ? 1 : config.sideOpacity;

            // Calculate rotation (tilt towards center: left card tilts right, right tilts left)
            const rotation = relativePos * config.sideRotation;

            // Z-index (center = highest)
            const zIndex = 10 - Math.abs(relativePos);

            const props = {
                x: x,
                y: y,
                scale: scale,
                opacity: isVisible ? opacity : 0,
                rotation: rotation,
                zIndex: zIndex,
                visibility: isVisible ? "visible" : "hidden",
            };

            if (animate) {
                gsap.to(item, {
                    ...props,
                    duration: config.duration,
                    ease: config.ease,
                });
            } else {
                gsap.set(item, props);
            }

            // Toggle active class
            item.classList.toggle("is-active", relativePos === 0);
        });
    }

    /**
     * Go to next item (with loop)
     */
    function goNext() {
        currentIndex = mod(currentIndex + 1, totalItems);
        updatePositions(true);
    }

    /**
     * Go to previous item (with loop)
     */
    function goPrev() {
        currentIndex = mod(currentIndex - 1, totalItems);
        updatePositions(true);
    }

    // Event listeners for navigation
    if (nextBtn) {
        nextBtn.addEventListener("click", goNext);
    }
    if (prevBtn) {
        prevBtn.addEventListener("click", goPrev);
    }

    // Pointer/Mouse drag support (without moving container)
    let pointerStartX = 0;
    let pointerStartY = 0;
    let isPointerDown = false;
    let hasDragged = false;

    container.addEventListener("pointerdown", (e) => {
        pointerStartX = e.clientX;
        pointerStartY = e.clientY;
        isPointerDown = true;
        hasDragged = false;
    });

    container.addEventListener("pointermove", (e) => {
        if (!isPointerDown) return;
        const deltaX = Math.abs(e.clientX - pointerStartX);
        const deltaY = Math.abs(e.clientY - pointerStartY);
        // Consider it a drag if moved more than 10px horizontally
        if (deltaX > 10 && deltaX > deltaY) {
            hasDragged = true;
            container.setPointerCapture(e.pointerId);
        }
    });

    container.addEventListener("pointerup", (e) => {
        if (!isPointerDown) return;
        isPointerDown = false;

        // Only handle as swipe if user actually dragged
        if (hasDragged) {
            const pointerEndX = e.clientX;
            const dragDistance = pointerEndX - pointerStartX;

            if (Math.abs(dragDistance) > config.dragThreshold) {
                if (dragDistance > 0) {
                    goPrev();
                } else {
                    goNext();
                }
            }
            e.preventDefault();
        }
        // If not dragged, the click will naturally propagate to the link
    });

    container.addEventListener("pointercancel", () => {
        isPointerDown = false;
        hasDragged = false;
    });

    // Touch swipe support
    let touchStartX = 0;

    container.addEventListener("touchstart", (e) => {
        touchStartX = e.touches[0].clientX;
    }, { passive: true });

    container.addEventListener("touchend", (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const swipeDistance = touchEndX - touchStartX;

        if (Math.abs(swipeDistance) > config.dragThreshold) {
            if (swipeDistance > 0) {
                goPrev();
            } else {
                goNext();
            }
        }
    }, { passive: true });

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
        const section = document.querySelector("#portfolio");
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;

        if (inView) {
            if (e.key === "ArrowRight") {
                goNext();
            } else if (e.key === "ArrowLeft") {
                goPrev();
            }
        }
    });

    // Initial positioning (no animation)
    updatePositions(false);
}
